import React, { useEffect, useMemo, useState, useRef } from "react";
import { defaultTimezone, ICrewMember } from "src/redux/reducers/CrewScheduleReducer";
import {
  toggleSelectedCrewMemberAction,
  toggleExpandedCrewRowAction,
  setSelectedCrewDays,
  setSelectedCrewEvents,
} from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import { openContextMenu } from "src/redux/actions/ContextMenu.Actions";
import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { Grid } from "@mui/material";
import { useDrag } from "react-dnd";
import { baseRowHeight, expandedRowHeight } from "src/pages/CrewSchedule/gl/CrewScheduleGL";
import { PositionBadge } from "../PositionBadge";
import CrewMemberName from "./CrewMemberName";
import { IconButton } from "../../IconButton/IconButton";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";
import { IFullPairingPosition, IPosition, ViolationCheckState } from "@vmosolutions/oc-interfaces";
import Tooltip from "@mui/material/Tooltip";
import { OpenFlyingIcons } from "../OpenFlyingIcons";
import { IRootState } from "src/redux/reducers/CombinedReducers";

interface ICrewMemberProps {
  crewMember: ICrewMember;
  positionStr?: string;
  isOnPairing?: boolean;
  positionIndex?: number;
  disableMouseEvents?: boolean;
  openFlyingDisplayIndex?: number; // Used to determine if position is selected in pairing editor.
  id?: string;
  desiredPosition?: IFullPairingPosition;
}

export interface ICrewDraggable {
  crewMember: ICrewMember;
  position: string;
  positionIndex: number;
  type: "CREW_ASSIGN" | "CREW_REASSIGN";
}

/**
 * A function that takes in two postions and returns the one with the highest display order
 * @param currentRecent-
 * @param incoming-
 */
const highestDisplayOrderPostion = (current: IPosition, incoming: IPosition): IPosition => {
  return current?.displayOrder > incoming?.displayOrder ? incoming : current;
};

export const CrewMember = (props: ICrewMemberProps): JSX.Element => {
  const {
    crewMember,
    disableMouseEvents,
    isOnPairing,
    id,
    openFlyingDisplayIndex,
    positionIndex,
    positionStr,
    desiredPosition,
  } = props;

  const { isSelected, isExpanded } = crewMember;
  const crewNameSpace = 80; // This accounts for the space required to display crew badges+padding

  const isICAO = useSelector((state: IRootState) => state.authentication.appSettings?.blockInfo?.icao);
  //!! Watch out because only a limited number of airports are stored in the redux store.
  const airportOptions = useSelector((state: IRootState) => state.structures.airportOptions);
  const [baseCode, setBaseCode] = useState(
    isICAO ? crewMember.operatingBase?.base.ICAO : airportOptions[crewMember.operatingBase?.base.ICAO]?.iata
  );

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (!crewMember?.operatingBase?.base?.ICAO) {
      setBaseCode(null);
      return;
    }
    if (isICAO) {
      setBaseCode(crewMember.operatingBase?.base.ICAO);
    } else {
      //it is possbile that the airportOptions object does not have the base ICAO as a key so default to the ICAO
      setBaseCode(airportOptions[crewMember.operatingBase?.base.ICAO]?.iata ?? crewMember.operatingBase?.base.ICAO);
    }
  }, [airportOptions, crewMember.operatingBase?.base.ICAO, isICAO]);

  const dispatch = useDispatch();

  const windowProperties = useSelector((state: IRootState) => state.crewSchedule.windowProperties[id]);
  const tz = windowProperties?.displayTimezone?.timezoneId ?? defaultTimezone.timezoneId;

  const toggleSelectedCrewMember = (crewInfoId: string) => (event: React.MouseEvent) => {
    dispatch(toggleSelectedCrewMemberAction(crewInfoId));
  };

  const toggleExpandCrewRow = (crewInfoId: string) => (event: React.MouseEvent) => {
    event.stopPropagation(); // Stop propagation of the select crew member action.
    dispatch(toggleExpandedCrewRowAction(crewInfoId));
  };

  const handleContext =
    (crewInfoId: string) =>
    (event: React.MouseEvent): void => {
      event.preventDefault();
      event.stopPropagation();
      //clear any selected crew sched events because I want to show the crew block hours
      dispatch(setSelectedCrewEvents([], false, false, false));
      dispatch(setSelectedCrewDays({ crewId: crewInfoId, selectedTimes: [], timezone: tz }));

      dispatch(toggleSelectedCrewMemberAction(crewInfoId, true));
      dispatch(openContextMenu(`${id}`));
    };

  // Check whether or not this position exists on the assigned crew member and return it.
  const positionOnCrew = crewMember?.crewInfo?.positions?.find((pos) => pos.title === positionStr);

  //  if a crew member doesn't have a position that matches the postionStr
  //  the crew's position with the highest display order is returned
  const positionFulfilled = positionOnCrew ?? crewMember?.crewInfo?.positions.reduce(highestDisplayOrderPostion);

  const dragItem: ICrewDraggable = useMemo(() => {
    return {
      crewMember,
      position: positionFulfilled?.title,
      positionIndex,
      type: isOnPairing ? "CREW_REASSIGN" : "CREW_ASSIGN",
    };
  }, [crewMember, positionFulfilled, positionIndex, isOnPairing]);

  const [collected, drag, dragPreview] = useDrag(
    () => ({
      type: isOnPairing ? "CREW_REASSIGN" : "CREW_ASSIGN",
      item: dragItem,
    }),
    [dragItem, isOnPairing]
  );

  const crewMemberStyle =
    !disableMouseEvents && isExpanded
      ? { display: "flex", height: expandedRowHeight }
      : { display: "flex", height: baseRowHeight };

  /** Generate crew position badge data. */
  const positionBadgeData = useMemo(() => {
    return !positionOnCrew && desiredPosition
      ? {
          position: desiredPosition.position,
          filled: true,
          error: " not found in assigned crew member qualifications.",
        }
      : { position: positionFulfilled, filled: true, isInactive: !crewMember?.crewInfo?.isActive };
  }, [positionOnCrew, desiredPosition, positionFulfilled, crewMember?.crewInfo?.isActive]);

  const isCrewOrPosSelected = useMemo(() => {
    return isSelected || (openFlyingDisplayIndex > -1 && openFlyingDisplayIndex === positionIndex);
  }, [positionIndex, isSelected, openFlyingDisplayIndex]);

  const violationCheckStatus = useSelector(
    (state: IRootState) => state.crewSchedule.violationCheckStatus[crewMember?._id]
  );

  const [scanAnimation, setScanAnimation] = useState<"scan_errors" | "scan_active" | "scan_success" | "">("");

  // Effect triggers state change for scanAnimation upon update of a CrewMember component's ViolationCheckStatus update
  // - Runs on: when the value of the crew member's ViolationCheckStatus lastState changes, but not on mount
  // - Side effect: sets isFirstRender to false, else updates the state of scanAnimation, which will trigger a CSS scanning animation
  useEffect(() => {
    const currentViolationCheckState: ViolationCheckState | null = violationCheckStatus?.lastState;

    if (isFirstRender.current) {
      isFirstRender.current = false;
      setScanAnimation("");
    } else {
      switch (currentViolationCheckState) {
        case ViolationCheckState.Inprogress:
          setScanAnimation("scan_active");
          break;
        case ViolationCheckState.Failed:
          setScanAnimation("scan_errors");
          break;
        case ViolationCheckState.Success:
          setScanAnimation("scan_success");
          break;
        default:
          setScanAnimation("");
      }
    }
  }, [violationCheckStatus]);

  useEffect(() => {
    // If success/error animation is playing, reset it back to neutral after 5 seconds.
    const successAnimationTimer = setTimeout(() => {
      if (scanAnimation === "scan_success" || scanAnimation === "scan_errors") {
        setScanAnimation("");
      }
    }, 5000);
    // clear timer on unmount if initiated
    return () => clearTimeout(successAnimationTimer);
  }, [scanAnimation]);

  // DISPLAY WHEN POSITION SLOTS ARE FULFILLED
  return (
    <Grid
      className={classNames(
        "crew_details",
        `position_block_${positionIndex}`,
        isCrewOrPosSelected && "selected",
        disableMouseEvents && "presentation",
        scanAnimation
      )}
      style={crewMemberStyle}
      key={crewMember._id}
      ref={drag}
      item
      id={`position_block_${positionIndex}`}
    >
      <Grid
        container
        alignItems={"flex-start"}
        wrap="nowrap"
        onClick={!disableMouseEvents ? toggleSelectedCrewMember(crewMember._id) : null}
        onContextMenu={!disableMouseEvents ? handleContext(crewMember._id) : null}
      >
        <Grid container direction="column" style={{ width: "auto" }}>
          <Grid item style={{ padding: 4 }}>
            <Tooltip
              enterDelay={500}
              placement="top"
              title={`${positionBadgeData?.position.description} ${positionBadgeData?.error ?? ""}`}
            >
              <div>
                <PositionBadge {...positionBadgeData} />
              </div>
            </Tooltip>
          </Grid>

          {!disableMouseEvents ? (
            <Grid item style={{ margin: "0px 5px" }}>
              <IconButton className="expand_crew_row_btn" onClick={toggleExpandCrewRow(crewMember._id)} size="large">
                {isExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Grid>
          ) : (
            <Grid item style={{ width: 14, height: 20, margin: "0px 8px" }}>
              <OpenFlyingIcons flyingStatusType={desiredPosition?.openFlyingStatus} />
            </Grid>
          )}
        </Grid>

        <Grid
          item
          className="crew_member"
          style={{
            height: isExpanded && !disableMouseEvents ? expandedRowHeight : baseRowHeight,
          }}
        >
          <CrewMemberName crewMemberName={crewMember.lastName} type="lastname" size={crewNameSpace} />
          <CrewMemberName crewMemberName={crewMember.firstName} type="firstname" size={crewNameSpace} />
          {!!crewMember && (
            <Grid
              container
              direction="row"
              style={{ width: "inherit", display: "flex", justifyContent: "space-between" }}
            >
              {crewMember?.operatingBase?.base?.ICAO && <span className="crew_base">{baseCode}</span>}
              {crewMember?.employeeId && <span className="crew_identity">{crewMember.employeeId}</span>}
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};
